import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import { pathOr } from 'ramda'
import { compose, defaultProps, withHandlers, withProps, withState } from 'recompose'

import withHeroText from 'components/HOCs/withHeroText'
import withPartnerProps from 'components/HOCs/WithPartnerProps'
import withReferralDisclosureRequired from 'components/HOCs/WithReferralDisclosureRequired'
import withRemoteStrings from 'components/HOCs/withRemoteStrings'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import createLaterContributionMutation from 'src/pages/StartInvesting/gql/createLaterContribution.mutation.gql'
import initialStateQuery from 'src/pages/StartInvesting/gql/initialState.query.gql'
import makeInvestmentMutation from 'src/pages/StartInvesting/gql/makeInvestment.mutation.gql'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { sendToSentry } from 'utils/errors'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { withOptimizely } from 'utils/Optimizely'

import { InnerProps, InvestmentMode } from '../../types'
import { readUserSettings, saveUserSettings } from '../../utilities'

export const DEFAULT_ONE_TIME = '5'

export const ONE_TIME_AMOUNTS_LATER = ['5', '25', '50', '100']

const clearUserSettings = () => saveUserSettings()

export const investmentAmount = (props: any) => {
  return props.oneTimeAmount || DEFAULT_ONE_TIME
}

const enhance = compose<InnerProps, {}>(
  defaultProps({
    loading: true,
    investmentMode: InvestmentMode.OneTime,
  }),
  withFlowRouter,
  withReferralDisclosureRequired(),
  withRouter,
  withHeroText,
  withPartnerProps,
  withRemoteStrings,
  withOptimizely,
  withState(
    'oneTimeAmount',
    'setOneTimeAmount',
    readUserSettings('oneTimeAmount', DEFAULT_ONE_TIME),
  ),
  withState('confirmModalOpen', 'setConfirmModalOpen', false),
  withState('noFundingSourceModalOpen', 'setNoFundingSourceModalOpen', false),
  withState('submitting', 'setSubmitting', false),
  graphql(makeInvestmentMutation, { name: 'makeInvestment' }),
  graphql(createLaterContributionMutation, { name: 'createLaterContribution' }),
  graphql(initialStateQuery, {
    options: {
      fetchPolicy: 'network-only',
    },
    props: ({ data }) => {
      return {
        loading: data.loading,
        fundingSource: pathOr(null, ['fundingSourceInfo'], data),
        oneTimeAmount: readUserSettings('oneTimeAmount', DEFAULT_ONE_TIME),
        investmentMode: readUserSettings('investmentMode', InvestmentMode.OneTime),
        laterAccountId: pathOr(null, ['laterAccounts', 0, 'accountId'], data),
      }
    },
  }),
  withProps(({ getRemoteString, heroText }) => {
    return {
      header: getRemoteString('later_onetime_investment_header', heroText.header),
      text: getRemoteString('later_onetime_investment_text', heroText.text),
    }
  }),
  withHandlers<InnerProps, {}>({
    handleCTA: ({
      setConfirmModalOpen,
      setNoFundingSourceModalOpen,
      fundingSource,
      flow,
      partner,
    }) => () => {
      if (fundingSource) {
        setConfirmModalOpen(true)
      } else {
        setNoFundingSourceModalOpen(true)
      }
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(
            TRACKER_IDS.START_INVESTING.LATER_ONBOARDING_ONE_TIME_START_INVESTING,
          ),
          ...getDefaultEventData('Button Tapped'),
        },
        flow,
        partner,
      })
    },
    handleSkip: ({ route, flow, partner }) => () => {
      saveUserSettings()

      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.START_INVESTING.LATER_ONBOARDING_ONE_TIME_SKIP),
          ...getDefaultEventData('Button Tapped'),
        },
        flow,
        partner,
      })
      route({
        from: FlowRoute.OneTimeInvestment,
        action: 'continue',
        state: null,
      })
    },
    handleLinkAccount: ({ route }) => () => {
      route({ from: FlowRoute.OneTimeInvestment, action: 'link-account' })
    },
    handleOneTimeAmountChange: ({
      setOneTimeAmount,
      recurringAmount,
      investmentMode,
      flow,
      partner,
    }) => ({ currentTarget }) => {
      saveUserSettings({
        oneTimeAmount: currentTarget.dataset.value,
        recurringAmount,
        investmentMode,
      })
      setOneTimeAmount(currentTarget.dataset.value)
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.START_INVESTING.CHANGE_ONE_TIME_AMOUNT),
          ...getDefaultEventData('Button Tapped'),
          cta_title: currentTarget.dataset.value,
        },
        flow,
        partner,
      })
    },
    handleModalClosed: ({ setConfirmModalOpen }) => () => {
      setConfirmModalOpen(false)
    },
    handleModalConfirmed: ({
      route,
      investmentMode,
      recurringAmount,
      oneTimeAmount,
      setConfirmModalOpen,
      setSubmitting,
      laterAccountId,
      createLaterContribution,
      trackOptimizely,
    }) => () => {
      setConfirmModalOpen(false)
      setSubmitting(true)

      const amount = parseFloat(
        investmentAmount({ investmentMode, recurringAmount, oneTimeAmount }),
      )

      createLaterContribution({
        variables: {
          accountId: laterAccountId,
          amount,
        },
      })
        .then(() => {
          clearUserSettings()
          trackOptimizely('one_time_investment.complete')
          route({
            from: FlowRoute.OneTimeInvestment,
            action: 'continue',
          })
        })
        .catch(err => {
          sendToSentry(err)
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
  }),
)

export default enhance
